import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import {
    Paper, TextField, Button, Typography, Divider, Chip, Avatar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, InputAdornment, CircularProgress, Dialog,
    DialogContent, DialogActions, Grow,
} from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import LinearProgress from '@material-ui/core/LinearProgress'
import Skeleton from '@material-ui/lab/Skeleton'
import SearchIcon from '@material-ui/icons/Search'
import TouchAppIcon from '@material-ui/icons/TouchApp'
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber'
import ClearIcon from '@material-ui/icons/Clear'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import VisibilityIcon from '@material-ui/icons/Visibility'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import DescriptionIcon from '@material-ui/icons/Description'
import FindInPageIcon from '@material-ui/icons/FindInPage'
import PersonIcon from '@material-ui/icons/Person'
import EventIcon from '@material-ui/icons/Event'
import LockIcon from '@material-ui/icons/Lock'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import SaveIcon from '@material-ui/icons/Save'
import ReplayIcon from '@material-ui/icons/Replay'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import HttpService from '../../services/httpService'
import { setSnackInfo } from '../../redux/actions/appAction'
import { maskPhoneNumbersInLogText } from '../../utils/index'

const ORANGE = '#F47E44'
const ORANGE_DARK = '#c1531f'
const ORANGE_LIGHT = '#fff1ea'
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`
const INK = '#3d2b21'
const GREEN = '#1e7e34'
const GREEN_LIGHT = '#e6f4ea'
const AMBER = '#b26a00'
const AMBER_LIGHT = '#fff3e0'

const RESULT_COLUMNS = 7

const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: 'rgba(193,83,31,0.10)' },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress)

const CompletionBar = withStyles(() => ({
    root: { height: 8, borderRadius: 999, backgroundColor: '#eceff3' },
    bar: { borderRadius: 999, backgroundColor: GREEN },
}))(LinearProgress)

const useStyles = makeStyles((theme) => ({
    root: { padding: '4px 4px 32px' },
    headerBanner: {
        display: 'flex', alignItems: 'center', gap: 16,
        background: HEADER_GRADIENT,
        borderRadius: 16, padding: '16px 26px', marginBottom: 16, color: '#fff',
        boxShadow: '0 8px 24px rgba(93,55,28,0.18)',
        [theme.breakpoints.down('xs')]: { padding: '16px 18px', flexWrap: 'wrap' },
    },
    headerIconWrap: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 46, height: 46, borderRadius: 14, flexShrink: 0,
        background: 'rgba(255,255,255,0.18)',
    },
    headerTitle: { fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1.3 },
    headerSubtitle: { fontSize: 13, color: 'rgba(255,255,255,0.85)', marginTop: 4, maxWidth: 720 },
    restrictedChip: {
        marginLeft: 'auto', flexShrink: 0,
        background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700,
        [theme.breakpoints.down('xs')]: { marginLeft: 0 },
    },
    progressSlot: { height: 4, marginBottom: 10 },
    loadingBanner: {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 14px', marginBottom: 14, borderRadius: 12,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`,
    },
    loadingBannerTitle: { fontSize: 13, fontWeight: 700, color: ORANGE_DARK },
    loadingBannerHint: { fontSize: 11.5, color: '#7a6a60', marginTop: 1 },
    card: {
        borderRadius: 16, border: '1px solid #eceff3',
        boxShadow: '0 4px 24px rgba(93,55,28,0.08)',
        padding: '18px 20px', marginBottom: 18,
    },
    sectionLabel: {
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 13, fontWeight: 700, color: ORANGE_DARK,
        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 14,
    },
    searchRow: { display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' },
    searchField: { flex: 1, minWidth: 300 },
    searchInputRoot: { borderRadius: 24, background: '#f7f9fc' },
    searchBtn: {
        background: ORANGE_DARK, color: '#fff', fontWeight: 700,
        borderRadius: 24, textTransform: 'none', padding: '8px 26px', marginTop: 1,
        boxShadow: '0 4px 14px rgba(193,83,31,0.25)',
        '&:hover': { background: '#a8441a' },
        '&.Mui-disabled': { background: 'rgba(193,83,31,0.45)', color: '#fff' },
    },
    tableWrap: { borderRadius: 12, border: '1px solid #eceff3', overflow: 'hidden' },
    headCell: {
        backgroundColor: ORANGE_DARK, color: '#fff', fontWeight: 700,
        fontSize: 12.5, letterSpacing: 0.3, borderBottom: 'none', whiteSpace: 'nowrap',
    },
    bodyCell: { fontSize: 13.5, color: INK },
    resultRow: {
        cursor: 'pointer', transition: 'background-color 150ms ease',
        '&:nth-of-type(even)': { backgroundColor: '#fafbfd' },
        '&:hover': { backgroundColor: ORANGE_LIGHT },
    },
    selectedRow: {
        backgroundColor: `${ORANGE_LIGHT} !important`,
        boxShadow: `inset 3px 0 0 ${ORANGE_DARK}`,
    },
    customerCell: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 },
    avatarChip: {
        width: 28, height: 28, fontSize: 12, fontWeight: 700,
        background: ORANGE_LIGHT, color: ORANGE_DARK,
    },
    openBtn: {
        borderRadius: 18, textTransform: 'none', fontWeight: 700,
        color: ORANGE_DARK, border: `1px solid ${ORANGE}66`,
        '&:hover': { background: ORANGE_LIGHT },
    },
    emptyState: { textAlign: 'center', padding: '54px 20px', color: '#9aa5b1' },
    emptyTitle: { marginTop: 10, fontWeight: 700, color: '#7c8695' },
    emptyHint: { color: '#aab3bf', fontSize: 13, marginTop: 2 },
    pickHint: {
        display: 'flex', alignItems: 'center', gap: 9,
        margin: '16px 0 0', padding: '10px 14px', borderRadius: 11,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}55`,
        fontSize: 12.5, color: '#8a4a1c', lineHeight: 1.5,
    },
    hintChips: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 },
    hintChip: { background: '#f4f6fb', color: '#6b7280', fontWeight: 600, fontSize: 12 },
    leadHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 },
    leadTitle: { fontSize: 20, fontWeight: 700, color: INK, lineHeight: 1.25 },
    leadMetaRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 },
    metaChip: { background: '#f4f6fb', color: '#4b5563', fontWeight: 600 },
    completionWrap: { minWidth: 230 },
    completionLabel: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        fontSize: 12, fontWeight: 700, color: '#7c8695', marginBottom: 6,
        textTransform: 'uppercase', letterSpacing: 0.4,
    },
    completionCount: { fontSize: 15, fontWeight: 700, color: INK, letterSpacing: 0 },
    warn: {
        display: 'flex', alignItems: 'center', gap: 10,
        background: AMBER_LIGHT, border: '1px solid #ffd79a', color: '#7a4b00',
        padding: '11px 14px', borderRadius: 12, marginBottom: 16, fontSize: 13,
    },
    docLabel: { display: 'flex', alignItems: 'center', gap: 9, fontWeight: 600, color: INK },
    missing: { color: '#9ca3af', fontStyle: 'italic', fontSize: 13 },
    linkBtn: { textTransform: 'none', fontWeight: 600, color: ORANGE_DARK, borderRadius: 18 },
    stagedCell: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
    actions: {
        marginTop: 20, display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
    },
    saveBtn: {
        background: ORANGE_DARK, color: '#fff', fontWeight: 700,
        borderRadius: 24, textTransform: 'none', padding: '8px 24px', minWidth: 150,
        boxShadow: '0 4px 14px rgba(193,83,31,0.25)',
        '&:hover': { background: '#a8441a' },
        '&.Mui-disabled': { background: '#e6e8ec', color: '#9aa5b1', boxShadow: 'none' },
    },
    discardBtn: {
        borderRadius: 24, textTransform: 'none', fontWeight: 600,
        color: ORANGE_DARK, border: `1px solid ${ORANGE}66`, padding: '7px 20px',
        '&:hover': { background: ORANGE_LIGHT },
    },
    dialogPaper: { borderRadius: 18, boxShadow: '0 20px 60px rgba(93,55,28,0.25)' },
    dialogHeader: {
        display: 'flex', alignItems: 'flex-start', gap: 14,
        padding: '18px 24px', color: '#fff', background: HEADER_GRADIENT,
    },
    dialogHeaderIcon: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 42, height: 42, borderRadius: 12, flexShrink: 0,
        background: 'rgba(255,255,255,0.2)',
    },
    dialogTitle: { fontSize: 18, fontWeight: 700, color: '#fff', lineHeight: 1.3 },
    dialogSubtitle: { fontSize: 12.5, color: 'rgba(255,255,255,0.85)', marginTop: 2 },
    replaceList: {
        background: '#f7f9fc', border: '1px solid #eceff3', borderRadius: 12,
        padding: '12px 16px', margin: '14px 0',
    },
    replaceItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: INK, padding: '3px 0' },
    viewerCloseBtn: {
        marginLeft: 'auto', color: '#fff', flexShrink: 0,
        transition: 'background-color 200ms ease, transform 200ms ease',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)', transform: 'rotate(90deg)' },
    },
    viewerBody: { padding: 0, background: '#f4f6fb', minHeight: 420 },
    // A fixed viewport height keeps a 40-page PDF and a passport photo in the
    // same frame instead of the dialog resizing around each file.
    viewerFrame: { width: '100%', height: '70vh', border: 'none', display: 'block', background: '#f4f6fb' },
    viewerImageWrap: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '70vh', padding: 16, boxSizing: 'border-box',
    },
    viewerImage: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 },
    viewerCentre: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 10, height: 420, textAlign: 'center', padding: '0 28px',
    },
    viewerNote: { fontSize: 14, fontWeight: 600, color: '#7c8695' },
    viewerHint: { fontSize: 12.5, color: '#aab3bf', maxWidth: 420 },
}))

const STATUS_STYLE = {
    staged: { background: AMBER_LIGHT, color: AMBER, label: 'Ready to replace', Icon: HourglassEmptyIcon },
    uploaded: { background: GREEN_LIGHT, color: GREEN, label: 'Uploaded', Icon: CheckCircleIcon },
    missing: { background: '#f1f2f5', color: '#6b7280', label: 'Not uploaded', Icon: RadioButtonUncheckedIcon },
}

const StatusChip = ({ state }) => {
    const s = STATUS_STYLE[state]
    return (
        <Chip
            size="small"
            icon={<s.Icon style={{ fontSize: 15, color: s.color }} />}
            label={s.label}
            style={{ background: s.background, color: s.color, fontWeight: 700 }}
        />
    )
}

const initials = (name) => String(name || '')
    .trim().split(/\s+/).slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('') || '?'

// Bot-created leads are named after the customer's mobile, so the name alone
// leaks it. Only the name reaches this page - the contact number is never
// selected by the route - so the masker falls back to its 10-digit rule.
const safeName = (name) => maskPhoneNumbersInLogText(name) || null

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB') : null)

// What a browser can render on its own. Anything outside these two groups
// (dwg, skp, xlsx, doc...) has no native viewer, so we say so rather than
// opening a blank frame.
//
// The two groups are handled completely differently, which is the whole trick:
//   image - an <img> sniffs the bytes itself and is NOT subject to CORS for
//           display, so it renders straight off the S3 url no matter what
//           Content-Type the bucket claims.
//   pdf   - an <iframe> obeys Content-Type, and these objects are stored
//           without one (services/s3.service.js sets no ContentType), so the
//           browser is told binary/octet-stream and downloads the file. The
//           only way to show it in place is to read the bytes and re-label
//           them - and that read does need the bucket to allow this origin.
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
const PDF_MIME = 'application/pdf'

const kindOf = (ext) => {
    if (ext === 'pdf') return 'pdf'
    if (IMAGE_EXTS.includes(ext)) return 'image'
    return null
}

// S3 keys carry a timestamp prefix and the original filename, and the url may
// end up with a query string or a fragment, so strip both before reading the
// extension. A url with no dot in its last segment returns the whole segment
// from .pop(), which would otherwise be printed back at the user as a file
// type - so anything that is not a short, plain word is treated as unknown.
const extensionOf = (url) => {
    const path = String(url || '').split('?')[0].split('#')[0]
    const last = path.split('/').pop() || ''
    if (!last.includes('.')) return ''
    const ext = last.split('.').pop().toLowerCase()
    return /^[a-z0-9]{1,5}$/.test(ext) ? ext : ''
}

// The file is uploaded to S3 first; only the returned url is saved on the lead.
const DesignSignOffDocuments = (props) => {
    const classes = useStyles()
    const [query, setQuery] = useState('')
    const [results, setResults] = useState([])
    const [searched, setSearched] = useState(false)
    const [searchedFor, setSearchedFor] = useState('')
    const [lead, setLead] = useState(null)
    const [staged, setStaged] = useState({})      // { [key]: newUrl } - not saved yet
    const [remarks, setRemarks] = useState('')
    // Only flips on blur or on a blocked save, so the field is not already red
    // the moment a lead is opened.
    const [remarksTouched, setRemarksTouched] = useState(false)

    // Local, per-action loaders instead of the global one, so an upload on one
    // row does not blank the whole screen and the rest stays usable.
    const [searching, setSearching] = useState(false)
    const [uploadingKey, setUploadingKey] = useState(null)
    const [saving, setSaving] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    // { label, url, ext, isNew } - the document currently open in the viewer.
    const [preview, setPreview] = useState(null)
    const [previewSrc, setPreviewSrc] = useState('')
    const [previewState, setPreviewState] = useState('idle')  // loading | ready | unsupported | error

    useEffect(() => {
        if (!preview) return undefined

        if (!preview.kind) {
            setPreviewState('unsupported')
            return undefined
        }

        // Images go straight to the <img>. No fetch, so nothing for the bucket's
        // CORS policy to block and nothing for its Content-Type to get wrong.
        if (preview.kind === 'image') {
            setPreviewSrc(preview.url)
            setPreviewState('ready')
            return undefined
        }

        let objectUrl
        let cancelled = false
        setPreviewState('loading')

        fetch(preview.url)
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                return res.blob()
            })
            .then((blob) => {
                if (cancelled) return
                objectUrl = URL.createObjectURL(new Blob([blob], { type: PDF_MIME }))
                setPreviewSrc(objectUrl)
                setPreviewState('ready')
            })
            .catch(() => {
                if (!cancelled) setPreviewState('error')
            })

        return () => {
            // Revoked on close and on every swap, or each preview leaks the
            // whole file into memory for the life of the tab.
            cancelled = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
            setPreviewSrc('')
        }
    }, [preview])

    const openPreview = (label, url, isNew = false) => {
        const ext = extensionOf(url)
        setPreview({ label, url, ext, kind: kindOf(ext), isNew })
    }

    // MUI keeps a Dialog's children mounted through the close transition, so
    // the state has to be reset together with the document - otherwise the last
    // file's branch is still live for 250ms with nothing behind it.
    const closePreview = () => {
        setPreview(null)
        setPreviewState('idle')
    }

    const searchLeads = async () => {
        if (query.trim().length < 3) {
            props.setSnackInfo('Type at least 3 characters to search', 'warning')
            return
        }
        const term = query.trim()
        try {
            setSearching(true)
            const res = await HttpService.searchDesignSignOffLeads(term)
            setResults(res.data.leads || [])
            setSearched(true)
            setSearchedFor(term)
            if (!res.data.leads?.length) props.setSnackInfo('No lead found for that search', 'warning')
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Something went wrong', 'error')
        }
        setSearching(false)
    }

    const clearSearch = () => {
        setQuery('')
        setResults([])
        setSearched(false)
        setSearchedFor('')
    }

    const selectLead = (row) => {
        setLead(row)
        setStaged({})
        setRemarks('')
        setRemarksTouched(false)
    }

    const uploadFile = async (e, docKey) => {
        const file = e.target.files[0]
        if (!file) return
        try {
            setUploadingKey(docKey)
            const formData = new FormData()
            formData.append('file', file)
            formData.append('customerName', `${lead.lead_no}_${lead.customerName || 'customer'}`)
            const res = await HttpService.wdFileUpload(formData)
            if (res.status === 200 && res.data) {
                setStaged((prev) => ({ ...prev, [docKey]: res.data }))
                props.setSnackInfo('File uploaded - click Save to apply it to the lead', 'success')
            } else {
                props.setSnackInfo('Upload failed, please try again', 'error')
            }
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Upload failed', 'error')
        }
        setUploadingKey(null)
        e.target.value = ''       // let the same file be picked again after a mistake
    }

    const discard = (docKey) => {
        const next = { ...staged }
        delete next[docKey]
        setStaged(next)
    }

    const save = async () => {
        // Also checked here, not only on the disabled button: the confirm dialog
        // stays open across state changes, so the button is not the only way in.
        if (!remarks.trim()) {
            setRemarksTouched(true)
            setConfirmOpen(false)
            props.setSnackInfo('Add a remark explaining why the file is being replaced', 'warning')
            return
        }
        setConfirmOpen(false)
        try {
            setSaving(true)
            const res = await HttpService.updateDesignSignOffDocs(lead._id, { documents: staged, remarks })
            setLead(res.data.lead)
            setStaged({})
            setRemarks('')
            setRemarksTouched(false)
            props.setSnackInfo(res.data.message || 'Documents updated', 'success')
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Could not update the documents', 'error')
        }
        setSaving(false)
    }

    const stagedKeys = Object.keys(staged)
    const stagedCount = stagedKeys.length
    const remarksMissing = !remarks.trim()

    // Names which of the two conditions is holding the save back, rather
    // than leaving a greyed-out button with no explanation.
    const saveBlockedReason = stagedCount === 0
        ? 'Upload a replacement file first'
        : remarksMissing
            ? 'Add a remark explaining why the file is being replaced'
            : ''

    const stagedLabels = useMemo(
        () => stagedKeys.map((k) => lead?.documents?.find((d) => d.key === k)?.label || k),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [staged, lead]
    )

    const uploadedCount = lead?.documents?.filter((d) => d.url).length || 0
    const totalDocs = lead?.documents?.length || 0
    const completionPct = totalDocs ? Math.round((uploadedCount / totalDocs) * 100) : 0

    const leadTitle = (row) => row.erpProjectNo || row.uniqueLeadNumber || `Lead ${row.lead_no}`

    return (
        <div className={classes.root}>
            <div className={classes.headerBanner}>
                <div className={classes.headerIconWrap}>
                    <DescriptionIcon />
                </div>
                <div>
                    <Typography className={classes.headerTitle}>
                        Re-upload Design Sign-Off Documents
                    </Typography>
                    <Typography className={classes.headerSubtitle}>
                        Replace a document that was uploaded wrongly at the Design Sign-Off stage. Every
                        replacement is recorded in the lead's Deal Activity along with the link it replaced.
                    </Typography>
                </div>
                <Chip
                    className={classes.restrictedChip}
                    size="small"
                    icon={<LockIcon style={{ fontSize: 15, color: '#fff' }} />}
                    label="Admin / Naraci Tech only"
                />
            </div>

            {/* Reserved slot, so starting a search does not jog the card down
                by 4px and back again. */}
            <div className={classes.progressSlot}>
                {searching ? <BrandLinearProgress /> : null}
            </div>

            {searching && (
                <div className={classes.loadingBanner}>
                    <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.loadingBannerTitle}>Searching leads…</div>
                        <div className={classes.loadingBannerHint}>
                            Matching “{query.trim()}” against project numbers and unique lead numbers.
                        </div>
                    </div>
                </div>
            )}

            <Paper className={classes.card} elevation={0}>
                <div className={classes.sectionLabel}>
                    <SearchIcon style={{ fontSize: 17 }} /> Find the lead
                </div>

                <div className={classes.searchRow}>
                    <TextField
                        className={classes.searchField}
                        size="small" variant="outlined"
                        // The route matches erpProjectNo and uniqueLeadNumber only,
                        // so the label no longer offers Lead No. and sets up a
                        // search that cannot be run.
                        label="Project No. / Unique Lead No."
                        placeholder="e.g. HS0426160"
                        helperText="Type at least 3 characters, then press Enter"
                        value={query}
                        disabled={searching}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchLeads()}
                        InputProps={{
                            classes: { root: classes.searchInputRoot },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" style={{ color: '#8a95a6' }} />
                                </InputAdornment>
                            ),
                            endAdornment: query && (
                                <InputAdornment position="end">
                                    <Tooltip title="Clear">
                                        <IconButton size="small" onClick={clearSearch}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        className={classes.searchBtn}
                        disabled={searching || query.trim().length < 3}
                        startIcon={searching
                            ? <CircularProgress size={15} thickness={5} style={{ color: '#fff' }} />
                            : <SearchIcon />}
                        onClick={searchLeads}
                    >
                        {searching ? 'Searching…' : 'Search'}
                    </Button>
                </div>

                {/* The rows have been clickable all along - only the Open
                    button looked like it was. Said once above the table, so
                    people do not hunt for the button on every row. */}
                {!searching && results.length > 0 && (
                    <div className={classes.pickHint}>
                        <TouchAppIcon style={{ fontSize: 17 }} />
                        <span>
                            <b>Click any row</b> to open that lead&apos;s Design Sign-Off documents
                            and replace the ones that are wrong.
                        </span>
                    </div>
                )}

                <div className={classes.tableWrap} style={{ marginTop: 16 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.headCell}>Project No.</TableCell>
                                    <TableCell className={classes.headCell}>Unique Lead No.</TableCell>
                                    <TableCell className={classes.headCell}>Customer</TableCell>
                                    <TableCell className={classes.headCell}>Current Stage</TableCell>
                                    <TableCell className={classes.headCell}>Lead No</TableCell>
                                    <TableCell className={classes.headCell}>Documents</TableCell>
                                    <TableCell className={classes.headCell} align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {searching ? (
                                    [...Array(4)].map((_, i) => (
                                        <TableRow key={`sk-${i}`}>
                                            {[...Array(RESULT_COLUMNS)].map((__, c) => (
                                                <TableCell key={c}><Skeleton height={20} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : results.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={RESULT_COLUMNS} style={{ borderBottom: 'none' }}>
                                            {/* Three different situations, three different
                                                things worth saying. */}
                                            <div className={classes.emptyState}>
                                                {searched ? (
                                                    <>
                                                        <FindInPageIcon style={{ fontSize: 46, color: '#c9d2de' }} />
                                                        <Typography className={classes.emptyTitle}>
                                                            Nothing matched “{searchedFor}”
                                                        </Typography>
                                                        <Typography className={classes.emptyHint}>
                                                            Check the project number, or try the unique lead number instead.
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        <SearchIcon style={{ fontSize: 46, color: '#c9d2de' }} />
                                                        <Typography className={classes.emptyTitle}>
                                                            Search for a project to begin
                                                        </Typography>
                                                        <Typography className={classes.emptyHint}>
                                                            Its Design Sign-Off documents will appear here, ready to replace.
                                                        </Typography>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    results.map((row) => {
                                        const uploaded = row.documents?.filter((d) => d.url).length || 0
                                        const total = row.documents?.length || 0
                                        const isSelected = lead && lead._id === row._id
                                        return (
                                            <TableRow
                                                key={row._id}
                                                hover
                                                className={`${classes.resultRow} ${isSelected ? classes.selectedRow : ''}`}
                                                onClick={() => selectLead(row)}
                                            >
                                                <TableCell className={classes.bodyCell} style={{ fontWeight: 700, color: ORANGE_DARK }}>
                                                    {row.erpProjectNo || '-'}
                                                </TableCell>
                                                <TableCell className={classes.bodyCell}>{row.uniqueLeadNumber || '-'}</TableCell>
                                                <TableCell className={classes.bodyCell}>
                                                    <div className={classes.customerCell}>
                                                        <Avatar className={classes.avatarChip}>
                                                            {initials(safeName(row.customerName))}
                                                        </Avatar>
                                                        <span>{safeName(row.customerName) || '-'}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className={classes.bodyCell}>{row.currentStage || '-'}</TableCell>
                                                <TableCell className={classes.bodyCell}>
                                                    {row.lead_no ?? '—'}
                                                </TableCell>
                                                <TableCell className={classes.bodyCell}>
                                                    <Chip
                                                        size="small"
                                                        label={`${uploaded} / ${total}`}
                                                        style={{
                                                            fontWeight: 700,
                                                            background: uploaded === total ? GREEN_LIGHT : '#f1f2f5',
                                                            color: uploaded === total ? GREEN : '#6b7280',
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                                    <Button
                                                        size="small"
                                                        className={classes.openBtn}
                                                        onClick={() => selectLead(row)}
                                                    >
                                                        {isSelected ? 'Opened' : 'Open'}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

                {!searched && !searching && (
                    <div className={classes.hintChips}>
                        <Chip size="small" className={classes.hintChip} label="Searches project numbers" />
                        <Chip size="small" className={classes.hintChip} label="Searches unique lead numbers" />
                        <Chip size="small" className={classes.hintChip} label="Shows the 20 most recent matches" />
                    </div>
                )}
            </Paper>

            {lead &&
                <Paper className={classes.card} elevation={0}>
                    <div className={classes.leadHead}>
                        <div style={{ minWidth: 0 }}>
                            <Typography className={classes.leadTitle}>{leadTitle(lead)}</Typography>
                            <div className={classes.leadMetaRow}>
                                {/* leadTitle only falls back to the lead number
                                    when BOTH the project number and the unique
                                    lead number are missing, so normally it was
                                    nowhere on this panel. `!= null` on purpose:
                                    lead_no is a number, and `||` would hide a
                                    genuine 0. */}
                                {lead.lead_no != null && (
                                    <Chip
                                        size="small" className={classes.metaChip}
                                        icon={<ConfirmationNumberIcon style={{ fontSize: 15 }} />}
                                        label={`Lead No. ${lead.lead_no}`}
                                    />
                                )}
                                {lead.uniqueLeadNumber && (
                                    <Chip
                                        size="small" className={classes.metaChip}
                                        label={`ULN ${lead.uniqueLeadNumber}`}
                                    />
                                )}
                                <Chip
                                    size="small" className={classes.metaChip}
                                    icon={<PersonIcon style={{ fontSize: 15 }} />}
                                    label={safeName(lead.customerName) || 'Customer not set'}
                                />
                                {lead.currentStage && (
                                    <Chip size="small" className={classes.metaChip} label={lead.currentStage} />
                                )}
                                {lead.designSignOffDate && (
                                    <Chip
                                        size="small" className={classes.metaChip}
                                        icon={<EventIcon style={{ fontSize: 15 }} />}
                                        label={`Signed off ${formatDate(lead.designSignOffDate)}`}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={classes.completionWrap}>
                            <div className={classes.completionLabel}>
                                <span>Documents on file</span>
                                <span className={classes.completionCount}>{uploadedCount} / {totalDocs}</span>
                            </div>
                            <CompletionBar variant="determinate" value={completionPct} />
                        </div>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />

                    {stagedCount > 0 &&
                        <div className={classes.warn}>
                            <ErrorOutlineIcon style={{ color: AMBER }} />
                            <span>
                                Nothing has been changed on the lead yet. Press <b>Save changes</b> to replace
                                {stagedCount === 1 ? ' the document.' : ` the ${stagedCount} documents.`}
                            </span>
                        </div>
                    }

                    <div className={classes.tableWrap}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.headCell} style={{ width: '28%' }}>Document</TableCell>
                                        <TableCell className={classes.headCell} style={{ width: '18%' }}>Status</TableCell>
                                        <TableCell className={classes.headCell} style={{ width: '26%' }}>Current file</TableCell>
                                        <TableCell className={classes.headCell}>Replace with</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lead.documents.map((doc) => {
                                        const isStaged = Boolean(staged[doc.key])
                                        const isUploading = uploadingKey === doc.key
                                        const state = isStaged ? 'staged' : doc.url ? 'uploaded' : 'missing'
                                        return (
                                            <TableRow key={doc.key} hover>
                                                <TableCell className={classes.bodyCell}>
                                                    <span className={classes.docLabel}>
                                                        <DescriptionIcon style={{ fontSize: 17, color: ORANGE_DARK }} />
                                                        {doc.label}
                                                    </span>
                                                </TableCell>

                                                <TableCell className={classes.bodyCell}>
                                                    <StatusChip state={state} />
                                                </TableCell>

                                                <TableCell className={classes.bodyCell}>
                                                    {doc.url
                                                        ? <div className={classes.stagedCell}>
                                                            <Button size="small" className={classes.linkBtn}
                                                                startIcon={<VisibilityIcon fontSize="small" />}
                                                                onClick={() => openPreview(doc.label, doc.url)}>
                                                                Preview
                                                            </Button>
                                                            <Tooltip title="Open in a new tab">
                                                                <IconButton size="small" onClick={() => window.open(doc.url)}>
                                                                    <OpenInNewIcon style={{ fontSize: 15 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </div>
                                                        : <span className={classes.missing}>Not uploaded</span>}
                                                </TableCell>

                                                <TableCell className={classes.bodyCell}>
                                                    {isStaged
                                                        ? <div className={classes.stagedCell}>
                                                            <CheckCircleIcon fontSize="small" style={{ color: GREEN }} />
                                                            <Button size="small" className={classes.linkBtn}
                                                                startIcon={<VisibilityIcon fontSize="small" />}
                                                                onClick={() => openPreview(doc.label, staged[doc.key], true)}>
                                                                Preview new file
                                                            </Button>
                                                            <Tooltip title="Discard this upload">
                                                                <IconButton size="small" onClick={() => discard(doc.key)}>
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </div>
                                                        : <>
                                                            <input
                                                                accept=".pdf,.jpg,.jpeg,.png,.dwg,.skp,.xlsx,.xls,.doc,.docx"
                                                                style={{ display: 'none' }}
                                                                id={`upload-${doc.key}`}
                                                                type="file"
                                                                disabled={isUploading || saving}
                                                                onChange={(e) => uploadFile(e, doc.key)}
                                                            />
                                                            <label htmlFor={`upload-${doc.key}`}>
                                                                <Button
                                                                    size="small" variant="outlined" component="span"
                                                                    className={classes.discardBtn}
                                                                    disabled={isUploading || saving}
                                                                    startIcon={isUploading
                                                                        ? <CircularProgress size={14} thickness={5} style={{ color: ORANGE_DARK }} />
                                                                        : <CloudUploadIcon fontSize="small" />}
                                                                >
                                                                    {isUploading ? 'Uploading…' : doc.url ? 'Re-upload' : 'Upload'}
                                                                </Button>
                                                            </label>
                                                        </>}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    <div className={classes.actions}>
                        <TextField
                            size="small" variant="outlined" style={{ flex: 1, minWidth: 280 }}
                            label="Remarks (why the file is being replaced)"
                            required
                            placeholder="e.g. Wrong revision uploaded at sign-off"
                            value={remarks}
                            disabled={saving}
                            error={remarksTouched && remarksMissing}
                            helperText={
                                remarksTouched && remarksMissing
                                    ? 'A remark is required before the documents can be replaced'
                                    : 'Saved to the Deal Activity alongside the replaced link'
                            }
                            onBlur={() => setRemarksTouched(true)}
                            onChange={(e) => setRemarks(e.target.value)}
                        />
                        {/* A disabled MUI button fires no mouse events, so the
                            tooltip needs a wrapper that is not disabled - without
                            the span the reason for the block is invisible. */}
                        <Tooltip title={saveBlockedReason}>
                            <span>
                                <Button
                                    className={classes.saveBtn}
                                    onClick={() => setConfirmOpen(true)}
                                    disabled={stagedCount === 0 || remarksMissing || saving}
                                    startIcon={saving
                                        ? <CircularProgress size={15} thickness={5} style={{ color: '#fff' }} />
                                        : <SaveIcon />}
                                >
                                    {saving ? 'Saving…' : 'Save changes'}
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            className={classes.discardBtn}
                            onClick={() => { setStaged({}); setRemarks(''); setRemarksTouched(false) }}
                            disabled={stagedCount === 0 || saving}
                            startIcon={<ReplayIcon />}
                        >
                            Discard all
                        </Button>
                    </div>
                </Paper>
            }

            {/* INLINE VIEWER */}
            <Dialog
                open={Boolean(preview)}
                onClose={closePreview}
                fullWidth maxWidth="md"
                classes={{ paper: classes.dialogPaper }}
                TransitionComponent={Grow}
                transitionDuration={250}
            >
                <div className={classes.dialogHeader}>
                    <div className={classes.dialogHeaderIcon}>
                        <VisibilityIcon />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <Typography className={classes.dialogTitle}>{preview?.label}</Typography>
                        <Typography className={classes.dialogSubtitle}>
                            {preview?.isNew ? 'New file — not saved to the lead yet' : 'Currently on the lead'}
                            {preview?.ext ? ` · .${preview.ext}` : ''}
                        </Typography>
                    </div>
                    <IconButton
                        className={classes.viewerCloseBtn}
                        size="small"
                        onClick={closePreview}
                    >
                        <ClearIcon />
                    </IconButton>
                </div>

                <DialogContent className={classes.viewerBody}>
                    {previewState === 'loading' && (
                        <div className={classes.viewerCentre}>
                            <CircularProgress size={28} thickness={5} style={{ color: ORANGE_DARK }} />
                            <Typography className={classes.viewerNote}>Loading preview…</Typography>
                        </div>
                    )}

                    {/* One `preview &&` guard for both branches. Splitting them
                        into `preview?.ext === 'pdf'` and `preview?.ext !== 'pdf'`
                        looked symmetrical but was not: once preview is null,
                        `undefined !== 'pdf'` is TRUE, so the image branch
                        rendered during the dialog's close transition and read
                        .label off null. */}
                    {previewState === 'ready' && preview && (
                        preview.kind === 'pdf' ? (
                            <iframe
                                title={preview.label}
                                src={previewSrc}
                                className={classes.viewerFrame}
                            />
                        ) : (
                            <div className={classes.viewerImageWrap}>
                                <img
                                    src={previewSrc}
                                    alt={preview.label}
                                    className={classes.viewerImage}
                                    onError={() => setPreviewState('error')}
                                />
                            </div>
                        )
                    )}

                    {previewState === 'unsupported' && (
                        <div className={classes.viewerCentre}>
                            <DescriptionIcon style={{ fontSize: 46, color: '#c9d2de' }} />
                            <Typography className={classes.viewerNote}>
                                {preview?.ext
                                    ? <>A <b>.{preview.ext}</b> file has no viewer built into the browser</>
                                    : 'This file type cannot be shown in the browser'}
                            </Typography>
                            <Typography className={classes.viewerHint}>
                                PDFs and images (JPG, PNG, GIF, WEBP, BMP) preview here. Drawings and
                                spreadsheets have to be opened in their own application.
                            </Typography>
                        </div>
                    )}

                    {previewState === 'error' && (
                        <div className={classes.viewerCentre}>
                            <ErrorOutlineIcon style={{ fontSize: 46, color: '#e8a8a1' }} />
                            <Typography className={classes.viewerNote}>
                                This file could not be loaded into the page
                            </Typography>
                            <Typography className={classes.viewerHint}>
                                {preview?.kind === 'pdf'
                                    ? 'Showing a PDF in place needs the storage bucket to allow this site to read it, and it currently does not. Opening it in a new tab still works.'
                                    : 'The file may have been moved or deleted from storage. Opening it in a new tab will show the exact error.'}
                            </Typography>
                        </div>
                    )}
                </DialogContent>

                <DialogActions style={{ padding: '14px 24px 20px' }}>
                    <Button className={classes.discardBtn} onClick={closePreview}>
                        Close
                    </Button>
                    <Button
                        className={classes.saveBtn}
                        startIcon={<OpenInNewIcon />}
                        onClick={() => preview && window.open(preview.url)}
                    >
                        Open in new tab
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Replaces window.confirm - a browser dialog cannot show which
                documents are about to be overwritten in a readable form. */}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                fullWidth maxWidth="xs"
                classes={{ paper: classes.dialogPaper }}
                TransitionComponent={Grow}
                transitionDuration={250}
            >
                <div className={classes.dialogHeader}>
                    <div className={classes.dialogHeaderIcon}>
                        <ErrorOutlineIcon />
                    </div>
                    <div>
                        <Typography className={classes.dialogTitle}>
                            Replace {stagedCount} document{stagedCount === 1 ? '' : 's'}?
                        </Typography>
                        <Typography className={classes.dialogSubtitle}>
                            {lead ? leadTitle(lead) : ''}
                        </Typography>
                    </div>
                </div>

                <DialogContent style={{ padding: '20px 24px 6px' }}>
                    <Typography variant="body2" style={{ color: '#4a5568' }}>
                        These documents will be replaced on the lead:
                    </Typography>

                    <div className={classes.replaceList}>
                        {stagedLabels.map((label) => (
                            <div className={classes.replaceItem} key={label}>
                                <DescriptionIcon style={{ fontSize: 16, color: ORANGE_DARK }} /> {label}
                            </div>
                        ))}
                    </div>

                    <Typography variant="body2" style={{ color: '#7c8695', fontSize: 12.5 }}>
                        The existing links are kept in the Deal Activity, so nothing is lost.
                    </Typography>
                </DialogContent>

                <DialogActions style={{ padding: '14px 24px 20px' }}>
                    <Button className={classes.discardBtn} onClick={() => setConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button className={classes.saveBtn} onClick={save} startIcon={<SaveIcon />}>
                        Replace &amp; save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default connect(null, { setSnackInfo })(DesignSignOffDocuments)
